import { SecondaryBtnColors } from '$lib/atoms/SecondaryButton.svelte'
import type { AccountingMailConfigData } from '$types/accountingMailConfigData'
import { DefaultAccountingMailConfigData } from '$types/accountingMailConfigData'
import { LotMailNotificationType } from '$types/enums'
import dayjs from 'dayjs'

const NUMERIC_STATE_REGEX = /^\d+$/

function computeMailDates(
	accountingMailData: AccountingMailConfigData['accountingMailData']
): Partial<Record<LotMailNotificationType, number>> {
	const result: Partial<Record<LotMailNotificationType, number>> = {}
	// normalise to midnight so adding days always lands at midnight of the target day
	// eslint-disable-next-line svelte/prefer-svelte-reactivity
	const baseDate = new Date()
	baseDate.setHours(0, 0, 0, 0)

	for (const mail of accountingMailData) {
		// state is a day-count string for scheduled mails; non-numeric states (e.g. "Versendet") are skipped
		if (!NUMERIC_STATE_REGEX.test(mail.state)) continue
		result[mail.mailType] = dayjs(baseDate).add(parseInt(mail.state), 'days').unix()
	}
	return result
}

export class AccountingMailConfigState {
	// --- raw data ---
	accountingMailConfigData: AccountingMailConfigData = $state(DefaultAccountingMailConfigData())
	allMailsSent = $state(false)
	allDispatchingMailsSent = $state(false)
	readOnly = $state(false)
	actualTargetComparisonFilled = $state(false)
	indexNextManualMail = $state(0)
	isMailDispatchingDisabled = $state(true)
	mailingList: string[] | null = $state(null)
	defaultReceiverList: string[] | undefined = $state(undefined)
	mailingListSize = $state(0)
	holidayDaysList: string[] = $state([])
	firstLotMailDurationDate: number | undefined = $state(undefined)
	secondLotMailDurationDate: number | undefined = $state(undefined)
	thirdLotMailDurationDate: number | undefined = $state(undefined)
	fourthLotMailDurationDate: number | undefined = $state(undefined)
	savedDates: Partial<Record<LotMailNotificationType, number>> = $state({})

	// --- data-only deriveds (no i18n needed) ---
	btnColorTriggerMailDispatching = $derived(
		this.isMailDispatchingDisabled ? SecondaryBtnColors.GREEN : SecondaryBtnColors.RED
	)
	disableSendMailButton = $derived(
		this.allMailsSent ||
			this.isMailDispatchingDisabled ||
			this.allDispatchingMailsSent ||
			this.actualTargetComparisonFilled ||
			this.readOnly
	)
	disableTriggerStartMailDispatchingButton = $derived(
		this.allMailsSent ||
			this.allDispatchingMailsSent ||
			this.actualTargetComparisonFilled ||
			this.readOnly
	)

	/** Populate all fields from a fresh API response. */
	fromData(data: AccountingMailConfigData): void {
		this.accountingMailConfigData = data
		this.mailingList = [...data.mailingList]
		this.defaultReceiverList = data.receiverList
		this.defaultReceiverList.forEach((receiver) => this.mailingList!.unshift(receiver))
		this.isMailDispatchingDisabled = data.isAccountingMailDispatchingDisabled
		// 5 = total number of accounting mails in a lot lifecycle
		this.allMailsSent = data.accountingMailData.filter((d) => d.dispatchDateMail).length === 5
		// dispatching is considered fully sent once the fourth (final dispatching) mail has been sent
		this.allDispatchingMailsSent =
			data.accountingMailData
				.filter((d) => d.mailType === LotMailNotificationType.FOURTH_MAIL)
				.filter((d) => d.dispatchDateMail).length === 1
		this.actualTargetComparisonFilled = data.actualTargetComparisonFilled
		this.readOnly = data.readOnly
		this.indexNextManualMail = this.getIndexNextManualMail(data)
		this.holidayDaysList = data.holidayDays

		const computed = computeMailDates(data.accountingMailData)
		this.savedDates = computed
		this.firstLotMailDurationDate = computed[LotMailNotificationType.FIRST_MAIL]
		this.secondLotMailDurationDate = computed[LotMailNotificationType.SECOND_MAIL]
		this.thirdLotMailDurationDate = computed[LotMailNotificationType.THIRD_MAIL]
		this.fourthLotMailDurationDate = computed[LotMailNotificationType.FOURTH_MAIL]
		this.mailingListSize = this.mailingList!.length
	}

	/**
	 * Re-initialise every field to its constructor default. The module-level singleton
	 * survives navigation; the page calls this on mount so a stale mailingList/mailingListSize
	 * mismatch from the previous lot cannot trigger a cross-lot updateMailingList POST.
	 */
	reset(): void {
		this.accountingMailConfigData = DefaultAccountingMailConfigData()
		this.allMailsSent = false
		this.allDispatchingMailsSent = false
		this.readOnly = false
		this.actualTargetComparisonFilled = false
		this.indexNextManualMail = 0
		this.isMailDispatchingDisabled = true
		this.mailingList = null
		this.defaultReceiverList = undefined
		this.mailingListSize = 0
		this.holidayDaysList = []
		this.firstLotMailDurationDate = undefined
		this.secondLotMailDurationDate = undefined
		this.thirdLotMailDurationDate = undefined
		this.fourthLotMailDurationDate = undefined
		this.savedDates = {}
	}

	private getIndexNextManualMail(data: AccountingMailConfigData) {
		// find the last already-sent/pending mail (excluding FIFTH_MAIL which is auto-sent),
		// then +1 gives the index of the next mail to send manually
		return (
			data.accountingMailData
				.filter((d) => d.mailType !== LotMailNotificationType.FIFTH_MAIL)
				.reduce(
					(lastSentIndex, mail, index) =>
						mail.dispatchDateMail && (mail.state === 'Versendet' || mail.state === 'Ausstehend')
							? index
							: lastSentIndex,
					-1 // initial value is -1, indicating no sent/pending mail found yet; +1 it would be 0, so the first mail
				) + 1
		)
	}
}

export const accountingMailConfigState: AccountingMailConfigState = $state(
	new AccountingMailConfigState()
)
